import { isSupabaseConfigured, supabase } from './supabase';
import type { QuestionCatalogRow, RawQuestionRecord, ServerImportReport } from './importTypes';

export const demoCatalog: QuestionCatalogRow[] = [
  {
    id: 'demo-1', external_id: 'QP-PG-L01-0001', level_name: 'Playgroup', subject_name: 'English', topic_name: 'Uppercase and lowercase',
    question_type: 'matching', language: 'en', difficulty: 1, status: 'draft', prompt: 'Match each capital letter to its small letter.',
    version_number: 1, review_count: 0, asset_count: 7, lot_id: 'PG-L01', updated_at: new Date().toISOString(), current_version_id: 'demo-v1',
  },
  {
    id: 'demo-2', external_id: 'QP-C2-L01-0001', level_name: 'Class 2', subject_name: 'Mathematics', topic_name: 'Place value',
    question_type: 'single_choice', language: 'en', difficulty: 2, status: 'awaiting_content_review', prompt: 'What is the place value of 5 in 352?',
    version_number: 1, review_count: 0, asset_count: 1, lot_id: 'C2-L01', updated_at: new Date().toISOString(), current_version_id: 'demo-v2',
  },
  {
    id: 'demo-3', external_id: 'QP-C4-L02-0042', level_name: 'Class 4', subject_name: 'Urdu', topic_name: 'عبارت فہمی',
    question_type: 'single_choice', language: 'ur', difficulty: 3, status: 'content_approved', prompt: 'درست جواب منتخب کریں۔',
    version_number: 1, review_count: 1, asset_count: 0, lot_id: 'C4-L02', updated_at: new Date().toISOString(), current_version_id: 'demo-v3',
  },
];

export async function fetchQuestionCatalog(search = '', status = 'all'): Promise<QuestionCatalogRow[]> {
  if (!isSupabaseConfigured) {
    const needle = search.toLowerCase();
    return demoCatalog.filter((row) => (status === 'all' || row.status === status) && Object.values(row).join(' ').toLowerCase().includes(needle));
  }
  let query = supabase.from('admin_question_catalog').select('*').order('updated_at', { ascending: false }).limit(250);
  if (status !== 'all') query = query.eq('status', status);
  if (search.trim()) query = query.or(`external_id.ilike.%${search.trim()}%,prompt.ilike.%${search.trim()}%`);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as QuestionCatalogRow[];
}

export async function createImportBatch(filename: string, format: string, totalRows: number): Promise<string> {
  const { data, error } = await supabase.rpc('create_question_import_batch', {
    p_filename: filename,
    p_source_format: format,
    p_total_rows: totalRows,
  });
  if (error) throw error;
  return String(data);
}

export async function stageImportRecords(batchId: string, records: RawQuestionRecord[], chunkSize = 100, onProgress?: (completed: number) => void) {
  for (let index = 0; index < records.length; index += chunkSize) {
    const chunk = records.slice(index, index + chunkSize);
    const { error } = await supabase.rpc('stage_question_import_chunk', {
      p_batch_id: batchId,
      p_start_row: index + 1,
      p_rows: chunk,
    });
    if (error) throw error;
    onProgress?.(Math.min(index + chunk.length, records.length));
  }
}

export async function validateServerBatch(batchId: string): Promise<ServerImportReport> {
  const { data, error } = await supabase.rpc('validate_question_import_batch', { p_batch_id: batchId });
  if (error) throw error;
  return data as ServerImportReport;
}

export async function commitServerBatch(batchId: string): Promise<{ committed_rows: number; skipped_rows: number }> {
  const { data, error } = await supabase.rpc('commit_question_import_batch', { p_batch_id: batchId });
  if (error) throw error;
  return data as { committed_rows: number; skipped_rows: number };
}

export async function submitForReview(questionId: string) {
  const { data, error } = await supabase.rpc('submit_question_for_review', { p_question_id: questionId });
  if (error) throw error;
  return String(data);
}

export async function recordReview(versionId: string, reviewType: string, decision: 'approved' | 'changes_requested' | 'rejected', notes = '') {
  const { data, error } = await supabase.rpc('record_question_review', {
    p_question_version_id: versionId,
    p_review_type: reviewType,
    p_decision: decision,
    p_issue_codes: [],
    p_notes: notes || null,
  });
  if (error) throw error;
  return String(data);
}

export async function publishQuestion(questionId: string) {
  const { data, error } = await supabase.rpc('publish_question_version', { p_question_id: questionId });
  if (error) throw error;
  return String(data);
}

export async function retireQuestion(questionId: string, reason: string) {
  const { data, error } = await supabase.rpc('retire_question', { p_question_id: questionId, p_reason: reason || null });
  if (error) throw error;
  return String(data);
}

export async function saveQuestionDraft(input: {
  questionId?: string | null;
  externalId: string;
  levelCode: string;
  subjectCode: string;
  topic: string;
  questionType: string;
  language: string;
  difficulty: number;
  prompt: string;
  options: unknown[];
  answerKey: unknown;
  explanation: string;
  tags: string[];
}) {
  const { data, error } = await supabase.rpc('save_question_draft', {
    p_question_id: input.questionId || null,
    p_external_id: input.externalId || null,
    p_level_code: input.levelCode,
    p_subject_code: input.subjectCode,
    p_topic: input.topic || null,
    p_question_type: input.questionType,
    p_language: input.language,
    p_difficulty: input.difficulty,
    p_prompt: input.prompt,
    p_options: input.options,
    p_answer_key: input.answerKey,
    p_explanation: input.explanation || null,
    p_stimulus: {},
    p_tags: input.tags,
  });
  if (error) throw error;
  return String(data);
}
